import React, { useState, useEffect } from 'react';
import Header from './components/Header';
import Footer from './components/Footer';
import UnifiedPromptBuilder from './components/UnifiedPromptBuilder';
import DocumentaryGenerator from './components/DocumentaryGenerator';
import ViralStoryGenerator from './components/ViralStoryGenerator';
import ModelComparison from './components/ModelComparison';
import AdvancedPromptEngineering from './components/AdvancedPromptEngineering';
import AIPoweredFeatures from './components/AIPoweredFeatures';
import UserDashboard from './components/UserDashboard';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import Contact from './pages/Contact';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentCancel from './pages/PaymentCancel';
import StripeTest from './pages/StripeTest';
import FirebaseInitializer from './components/FirebaseInitializer';
import { ApiKeyProvider } from './contexts/ApiKeyContext';
import { AuthProvider } from './contexts/AuthContext';
import { Toaster } from 'react-hot-toast';
import './App.css';
import './styles/globals.css';

function App() {
  const [darkMode, setDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState<'dashboard' | 'builder' | 'documentary' | 'viralStory' | 'comparison' | 'advanced' | 'aiPowered'>('dashboard');
  const [activePage, setActivePage] = useState<'app' | 'privacy' | 'terms' | 'contact' | 'payment-success' | 'payment-cancel' | 'stripe-test'>('app');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Tab configuration with better names and metadata
  const tabs = [
    { id: 'dashboard', name: 'Dashboard', icon: '📊', color: 'cyan', group: null },
    { id: 'builder', name: 'Prompt Builder', icon: '🎨', color: 'sky', group: 'create', description: 'Templates & custom prompts' },
    { id: 'aiPowered', name: 'Prompt Studio', icon: '🚀', color: 'cyan', group: 'ai', badge: 'HOT', description: 'Enhance prompts & media-to-prompt' },
    { id: 'documentary', name: 'Storyboard', icon: '🎬', color: 'teal', group: 'ai', description: 'Generate story to image & video prompts' },
    { id: 'viralStory', name: 'Viral Stories', icon: '🔥', color: 'purple', group: 'ai', badge: 'NEW', description: '1M+ views story generator' },
    { id: 'advanced', name: 'Prompt Engineering', icon: '🧠', color: 'sky', group: 'advanced', description: 'Advanced techniques' },
    { id: 'comparison', name: 'Model Comparison', icon: '🤖', color: 'cyan', group: 'advanced', badge: 'BETA', description: 'Compare AI models' },
  ];

  const getTabName = (tabId: string) => {
    return tabs.find(t => t.id === tabId)?.name || tabId;
  };

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
    }
  }, [darkMode]);

  // Set document title and check for payment redirect
  useEffect(() => {
    document.title = 'FXPrompt - Professional AI Prompt Generator';
    
    // Check if we're coming from Stripe payment or test page
    const path = window.location.pathname;
    if (path === '/payment-success') {
      setActivePage('payment-success');
    } else if (path === '/payment-cancel') {
      setActivePage('payment-cancel');
    } else if (path === '/stripe-test') {
      setActivePage('stripe-test');
    }
  }, []);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <FirebaseInitializer>
    <AuthProvider>
      <ApiKeyProvider>
        <div className={`min-h-screen flex flex-col transition-colors duration-200 ${darkMode ? 'dark bg-slate-900' : 'bg-gray-50'}`}>
          <Toaster 
            position="top-center"
            toastOptions={{
              duration: 4000,
              style: {
                background: '#363636',
                color: '#fff',
              },
              success: {
                style: {
                  background: '#10b981',
                },
                iconTheme: {
                  primary: 'white',
                  secondary: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
              },
            }}
          />
          <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      
      {/* Mobile Dropdown Menu */}
      {activePage === 'app' && (
      <div className="md:hidden max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
        <div className="relative">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="w-full px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm flex items-center justify-between hover:bg-gray-50 transition-colors"
          >
            <span className="flex items-center gap-2">
              <span>{tabs.find(t => t.id === activeTab)?.icon}</span>
              <span className="font-medium">{getTabName(activeTab)}</span>
            </span>
            <svg className={`w-5 h-5 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {mobileMenuOpen && (
            <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => {
                    setActiveTab(tab.id as any);
                    setMobileMenuOpen(false);
                  }}
                  className={`w-full px-4 py-3 text-left flex items-center justify-between hover:bg-gray-50 transition-colors ${
                    activeTab === tab.id ? 'bg-gray-50 border-l-4 border-' + tab.color + '-600' : ''
                  }`}
                >
                  <div>
                    <div className="flex items-center gap-2">
                      <span>{tab.icon}</span>
                      <span className="font-medium">{tab.name}</span>
                      {tab.badge && (
                        <span className={`px-1.5 py-0.5 text-xs bg-gradient-to-r ${
                          tab.badge === 'HOT' ? 'from-purple-500 to-pink-500' : 'from-green-500 to-emerald-500'
                        } text-white rounded-full font-semibold`}>
                          {tab.badge}
                        </span>
                      )}
                    </div>
                    {tab.description && (
                      <p className="text-xs text-gray-500 mt-1 ml-7">{tab.description}</p>
                    )}
                  </div>
                  {activeTab === tab.id && (
                    <svg className="w-5 h-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Desktop Tab Navigation - Modern Design */}
      {activePage === 'app' && (
      <div className="hidden md:block bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center gap-6">
            {/* Dashboard */}
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'dashboard'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Dashboard</span>
              </span>
              {activeTab === 'dashboard' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>

            {/* Prompt Builder */}
            <button
              onClick={() => setActiveTab('builder')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'builder'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
                </svg>
                <span>Prompt Builder</span>
              </span>
              {activeTab === 'builder' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>

            {/* Prompt Studio */}
            <button
              onClick={() => setActiveTab('aiPowered')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'aiPowered'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span>Prompt Studio</span>
              </span>
              {activeTab === 'aiPowered' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>

            {/* Storyboard */}
            <button
              onClick={() => setActiveTab('documentary')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'documentary'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                <span>Storyboard</span>
              </span>
              {activeTab === 'documentary' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>

            {/* Viral Stories */}
            <button
              onClick={() => setActiveTab('viralStory')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'viralStory'
                  ? 'text-purple-600 dark:text-purple-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 18.657A8 8 0 016.343 7.343S7 9 9 10c0-2 .5-5 2.986-7C14 5 16.09 5.777 17.656 7.343A7.975 7.975 0 0120 13a7.975 7.975 0 01-2.343 5.657z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.879 16.121A3 3 0 1012.015 11L11 14H9c0 .768.293 1.536.879 2.121z" />
                </svg>
                <span>Viral Stories</span>
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300 rounded-full">NEW</span>
              </span>
              {activeTab === 'viralStory' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500"></div>
              )}
            </button>

            {/* Engineering */}
            <button
              onClick={() => setActiveTab('advanced')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'advanced'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                <span>Engineering</span>
              </span>
              {activeTab === 'advanced' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>

            {/* Compare */}
            <button
              onClick={() => setActiveTab('comparison')}
              className={`relative px-1 py-4 font-medium transition-all duration-200 group ${
                activeTab === 'comparison'
                  ? 'text-cyan-600 dark:text-cyan-400'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
              }`}
            >
              <span className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                <span>Compare</span>
              </span>
              {activeTab === 'comparison' && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-cyan-500 to-sky-500"></div>
              )}
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Content - flex-grow to push footer down */}
      <main className="flex-grow">
        {activePage === 'app' && (
          <>
            {activeTab === 'dashboard' && <UserDashboard />}
            {activeTab === 'builder' && <UnifiedPromptBuilder />}
            {activeTab === 'documentary' && <DocumentaryGenerator />}
            {activeTab === 'viralStory' && <ViralStoryGenerator />}
            {activeTab === 'comparison' && <ModelComparison />}
            {activeTab === 'advanced' && <AdvancedPromptEngineering />}
            {activeTab === 'aiPowered' && <AIPoweredFeatures />}
          </>
        )}
        {activePage === 'privacy' && <PrivacyPolicy onBack={() => setActivePage('app')} />}
        {activePage === 'terms' && <TermsOfService onBack={() => setActivePage('app')} />}
        {activePage === 'contact' && <Contact onBack={() => setActivePage('app')} />}
        {activePage === 'payment-success' && <PaymentSuccess onBack={() => setActivePage('app')} />}
        {activePage === 'payment-cancel' && <PaymentCancel onBack={() => setActivePage('app')} />}
        {activePage === 'stripe-test' && <StripeTest />}
      </main>
      
      {/* Footer - Hide on payment pages */}
      {!['payment-success', 'payment-cancel'].includes(activePage) && (
        <Footer onNavigate={(page) => setActivePage(page as any)} />
      )}
      </div>
      </ApiKeyProvider>
    </AuthProvider>
    </FirebaseInitializer>
  );
}

export default App;
